import { Link } from "@tanstack/react-router";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";

export interface City {
  id: string;
  slug: string;
  name: string;
  image?: string;
  activitiesCount?: number;
}

/**
 * The pewter-framed city photo card. Shared by the home "Explore by city" row
 * and the all-cities page (/$lang/place), so the two never drift.
 */
export function CityCard({
  city,
  locale,
  eager = false,
  sizes = "(max-width: 640px) 41vw, (max-width: 1024px) 25vw, 240px",
}: {
  readonly city: City;
  readonly locale: string;
  /** The first cards on screen fetch ahead of everything below them. */
  readonly eager?: boolean;
  readonly sizes?: string;
}) {
  return (
    <Link
      to="/$lang/place/$city"
      params={{ lang: locale, city: city.slug }}
      className="group relative block overflow-hidden rounded-[24px] p-[3px] transition hover:-translate-y-1"
      style={{
        background:
          "linear-gradient(155deg, rgba(255,255,255,1) 0%, rgba(180,184,210,0.55) 28%, rgba(17,14,57,0.18) 55%, rgba(180,184,210,0.55) 78%, rgba(255,255,255,1) 100%)",
        boxShadow: "0 10px 24px -18px rgba(17,14,57,0.18)",
      }}
    >
      <div className="relative overflow-hidden rounded-[21px] shadow-[inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-1px_0_rgba(17,14,57,0.15)]">
        <div className="aspect-[4/5] w-full overflow-hidden">
          <ImageWithFallback
            src={city.image}
            alt={city.name}
            sizes={sizes}
            loading={eager ? "eager" : "lazy"}
            fetchPriority={eager ? "high" : "auto"}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.06]"
          />
        </div>
        <div
          className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 px-3 pb-3 pt-8 sm:px-4 sm:pb-4"
          style={{
            background:
              "linear-gradient(to top, rgba(17,14,57,0.55) 0%, rgba(17,14,57,0.0) 100%)",
          }}
        >
          <span className="font-heading text-[17px] font-bold leading-tight text-white drop-shadow sm:text-[20px]">
            {city.name}
          </span>
        </div>
      </div>
    </Link>
  );
}
